import React, { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";
import { LOGOS } from "@/lib/api";
import AdminDashboard from "@/components/admin/AdminDashboard";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Admin() {
  const [authState, setAuthState] = useState(null); // null=checking, true, false
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const run = async () => {
      if (hash.includes("session_id=")) {
        const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
        window.history.replaceState(null, "", window.location.pathname);
        try {
          const { data } = await adminApi.post(
            "/auth/session",
            {},
            { headers: { "X-Session-ID": sid } }
          );
          setUser(data);
          setAuthState(true);
        } catch (e) {
          setError(
            e?.response?.status === 403
              ? "This Google account is not authorised for admin access."
              : "Sign-in failed. Please try again."
          );
          setAuthState(false);
        }
        return;
      }
      try {
        const { data } = await adminApi.get("/auth/me");
        setUser(data);
        setAuthState(true);
      } catch {
        setAuthState(false);
      }
    };
    run();
  }, []);

  const login = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await adminApi.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed:", e);
    }
    setUser(null);
    setAuthState(false);
  };

  if (authState === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-50/40" data-testid="admin-loading">
        <Loader2 className="h-10 w-10 text-brand animate-spin" />
      </div>
    );
  }

  if (authState === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-50/40 px-5">
        <div className="max-w-sm w-full bg-white border border-brand/10 rounded-2xl p-10 text-center shadow-sm" data-testid="admin-login">
          <img src={LOGOS.wordmarkGreen} alt="Jero-One" className="h-12 w-auto object-contain mx-auto mb-6" />
          <div className="h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-6 w-6 text-brand" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-brand">Admin Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Sign in with your authorised Jero-One Google account.
          </p>
          {error && (
            <p className="mt-4 text-sm text-destructive bg-destructive/5 rounded-lg px-3 py-2" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <Button
            data-testid="admin-google-login"
            onClick={login}
            className="mt-6 w-full rounded-full bg-brand hover:bg-brand-700 text-white h-12"
          >
            <LogIn className="h-4 w-4 mr-2" /> Continue with Google
          </Button>
        </div>
      </div>
    );
  }

  return <AdminDashboard user={user} onLogout={logout} />;
}
